import React from "react";
import TextEditor from "./components/TextEditor";
import HeroSection from "./components/HeroSection";
import Video from "./components/Video";
import FeaturedProductsSection from "./components/FeaturedProductsSection";
import TwoColumns from "./components/TwoColumns";
import Content from "./components/Content";
import FAQ from "./components/FAQ";
import ImageSlider from "./components/ImageSlider";
import ColumnImage from "./components/ColumnImage";
import CardLinks from "./components/CardLinks";
import ContentCard from "./components/ContentCard";
import ImageCard from "./components/ImageCard";
import MasonryImages from "./components/MasonryImages";
import PageHeader from "./components/PageHeader";
import IconContent from "./components/IconContent";
import Section from "@/components/ui/Section";
import Wrapper from "@/components/ui/Wrapper";
import ContentSimple from "./components/ContentSimple";
import PostImage from "./components/PostImage";
import PostContent from "./components/PostContent";
import ContactCard from "./components/ContactCard";
import PostsGrid from "./components/PostsGrid";
import LatestPosts from "./components/LatestPosts";
import FeatureHighlights from "./components/FeatureHighlights";
import FeaturedEvents from "./components/FeaturedEvents";
import EventsGrid, { type EventsGridFields } from "./components/EventsGrid";
import type {
  ACFBuilder,
  ACFNestedBase,
  TextEditorData,
  HeroSectionFields,
  VideoFields,
  FeaturedProductsFields,
  TwoColumnsFields,
  ContentFields,
  ImageSliderFields,
  ColumnImageFields,
  CardLinksFields,
  ImageCardFields,
  MasonryImagesFields,
  PageHeaderFields,
  ContentCardFields,
  IconContentFields,
  ContentSimpleFields,
  PostImageFields,
  FAQFields,
  PostContentFields,
  ContactCardFields,
  PostsGridFields,
  LatestPostsFields,
  FeatureHighlightsFields,
  FeaturedEventsFields,
} from "@/types/acf";

type RenderableBase = ACFNestedBase & { hide?: boolean; id?: string };

type LatestPostsBlock = { latest_posts?: LatestPostsFields & RenderableBase };
type PostsGridBlock = { posts_grid?: PostsGridFields & RenderableBase };
type EventsGridBlock = { events_grid?: EventsGridFields & RenderableBase };
type FeatureHighlightsBlock = { key_features?: FeatureHighlightsFields & RenderableBase };
type ContactCardBlock = { contact_card?: ContactCardFields & RenderableBase };

/* =========================================================
   SAFE UNIQUE KEY GENERATOR
   ========================================================= */
function safeKey(layout: string, payloadId: unknown, index: number) {
  const id = typeof payloadId === "string" ? payloadId.trim() : "";
  return id ? `${layout}-${id}` : `${layout}-${index}`;
}

/* =========================================================
   WRAPPER HELPER
   ========================================================= */
function wrap(
  inColumns: boolean,
  payload: { id?: string } | undefined,
  child: React.ReactNode,
  opts?: {
    key: React.Key;
    tag?: "section" | "div";
    useWrapper?: boolean;
    outerClassName?: string;
  }
) {
  const key = opts?.key;

  if (inColumns) return <React.Fragment key={key}>{child}</React.Fragment>;

  const tag = opts?.tag ?? "section";
  const useWrapper = opts?.useWrapper ?? true;
  const outerClassName = opts?.outerClassName;
  const id = payload?.id;

  const inner = useWrapper ? <Wrapper>{child}</Wrapper> : child;

  if (tag === "div") {
    return (
      <div key={key} id={id} className={outerClassName}>
        {inner}
      </div>
    );
  }

  return (
    <Section key={key} id={id} className={outerClassName}>
      {inner}
    </Section>
  );
}

/* =========================================================
   PAGE BUILDER
   ========================================================= */
export default function PageBuilder({
  data,
  inColumns = false,
  lang,
}: {
  data: ACFBuilder[];
  inColumns?: boolean;
  lang?: string;
}) {
  if (!Array.isArray(data) || data.length === 0) return null;
  return (
    <>
      {data.map((block, index) => {
        switch (block.acf_fc_layout) {
          case "text_editor": {
            const payload = block.text_editor as (TextEditorData & RenderableBase) | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return wrap(inColumns, payload, <TextEditor data={payload} />, {
              key,
              tag: "section",
              useWrapper: true,
              outerClassName: "text-editor-layout",
            });
          }

          case "latest_posts": {
            if (inColumns) return null;
            const payload = (block as LatestPostsBlock).latest_posts as
              | (LatestPostsFields & RenderableBase)
              | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return wrap(inColumns, payload, <LatestPosts data={payload} lang={lang} />, {
              key,
              tag: "section",
              useWrapper: true,
            });
          }

          case "hero_section": {
            const payload = block.hero_section as (HeroSectionFields & RenderableBase) | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return wrap(inColumns, payload, <HeroSection data={payload} />, {
              key,
              tag: "div",
              useWrapper: false,
              outerClassName: "m-0 p-0 w-full hero-slider-layout",
            });
          }

          case "feature_highlights": {
            const payload = (block as FeatureHighlightsBlock).key_features as
              | (FeatureHighlightsFields & RenderableBase)
              | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return (
              <React.Fragment key={key}>
                <FeatureHighlights data={payload} />
              </React.Fragment>
            );
          }

          case "faq": {
            if (inColumns) return null;
            const payload = block.faq as (FAQFields & RenderableBase) | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return wrap(inColumns, payload, <FAQ data={payload} />, {
              key,
              tag: "section",
              useWrapper: true,
            });
          }

          case "content_simple": {
            if (inColumns) return null;
            const payload = block.content_simple as
              | (ContentSimpleFields & RenderableBase)
              | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return wrap(inColumns, payload, <ContentSimple data={payload} />, {
              key,
              tag: "section",
              useWrapper: true,
            });
          }

          case "post_image": {
            if (inColumns) return null;
            const payload = block.post_image as (PostImageFields & RenderableBase) | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return wrap(inColumns, payload, <PostImage data={payload} />, {
              key,
              tag: "section",
              useWrapper: true,
              outerClassName: "px-0 mt-0 mb-10",
            });
          }

          case "post_content": {
            if (inColumns) return null;
            const payload = block.post_content as
              | (PostContentFields & RenderableBase)
              | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return wrap(inColumns, payload, <PostContent data={payload} />, {
              key,
              tag: "section",
              useWrapper: true,
              outerClassName: "px-0 mt-0 mb-10",
            });
          }

          case "content": {
            const payload = block.content as (ContentFields & RenderableBase) | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return wrap(inColumns, payload, <Content data={payload} />, {
              key,
              tag: "section",
              useWrapper: true,
            });
          }

          case "image_slider": {
            const payload = block.image_slider as
              | (ImageSliderFields & RenderableBase)
              | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return wrap(inColumns, payload, <ImageSlider data={payload} />, {
              key,
              tag: "section",
              useWrapper: true,
              outerClassName: "overflow-x-hidden m-0 p-0",
            });
          }

          case "video": {
            const payload = block.video as (VideoFields & RenderableBase) | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return wrap(inColumns, payload, <Video data={payload} />, {
              key,
              tag: "section",
              useWrapper: true,
            });
          }

          case "posts_grid": {
            if (inColumns) return null;
            const payload = (block as PostsGridBlock).posts_grid as
              | (PostsGridFields & RenderableBase)
              | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return wrap(inColumns, payload, <PostsGrid data={payload} lang={lang} />, {
              key,
              tag: "section",
              useWrapper: true,
            });
          }

          case "events_grid": {
            if (inColumns) return null;
            const payload = (block as EventsGridBlock).events_grid as
              | (EventsGridFields & RenderableBase)
              | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return wrap(inColumns, payload, <EventsGrid data={payload} lang={lang} />, {
              key,
              tag: "section",
              useWrapper: true,
            });
          }

          case "page_header": {
            const payload = block.page_header as (PageHeaderFields & RenderableBase) | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return wrap(inColumns, payload, <PageHeader data={payload} />, {
              key,
              tag: "section",
              useWrapper: true,
            });
          }

          case "featured_products": {
            const payload = block.featured_products as
              | (FeaturedProductsFields & RenderableBase)
              | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return wrap(inColumns, payload, <FeaturedProductsSection data={payload} />, {
              key,
              tag: "section",
              useWrapper: true,
              outerClassName: "featured-products-section",
            });
          }

          case "two_columns": {
            const payload = block.two_columns as (TwoColumnsFields & RenderableBase) | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return wrap(inColumns, payload, <TwoColumns data={payload} />, {
              key,
              tag: "section",
              useWrapper: true,
            });
          }

          case "icon_content": {
            if (inColumns) return null;
            const payload = block.icon_content as
              | (IconContentFields & RenderableBase)
              | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return wrap(inColumns, payload, <IconContent data={payload} />, {
              key,
              tag: "section",
              useWrapper: true,
            });
          }

          // ── featured_events: async Server Component, lang forwarded ──────────
          case "featured_events": {
            if (inColumns) return null;
            const payload = block.featured_events as
              | (FeaturedEventsFields & RenderableBase)
              | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            // Pass lang so the component never needs useLayoutData() / useEffect
            return wrap(
              inColumns,
              payload,
              <FeaturedEvents data={payload} lang={lang} />,
              { key, tag: "section", useWrapper: true }
            );
          }

          case "contact_card": {
            const payload = (block as ContactCardBlock).contact_card as
              | (ContactCardFields & RenderableBase)
              | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return wrap(inColumns, payload, <ContactCard data={payload} />, {
              key,
              tag: "section",
              useWrapper: true,
            });
          }

          case "column_image": {
            const payload = block.column_image as
              | (ColumnImageFields & RenderableBase)
              | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return (
              <React.Fragment key={key}>
                <ColumnImage data={payload} />
              </React.Fragment>
            );
          }

          case "content_card": {
            const payload = block.content_card as
              | (ContentCardFields & RenderableBase)
              | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return (
              <React.Fragment key={key}>
                <ContentCard data={payload} />
              </React.Fragment>
            );
          }

          case "card_links": {
            const payload = block.card_links as (CardLinksFields & RenderableBase) | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return (
              <React.Fragment key={key}>
                <CardLinks data={payload} />
              </React.Fragment>
            );
          }

          case "image_card": {
            const payload = block.image_card as (ImageCardFields & RenderableBase) | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return wrap(
              inColumns,
              payload,
              <ImageCard data={payload} inColumns={inColumns} />,
              { key, tag: "section", useWrapper: true }
            );
          }

          case "masonry_images": {
            const payload = block.masonry_images as
              | (MasonryImagesFields & RenderableBase)
              | undefined;
            if (!payload || payload.hide) return null;
            const key = safeKey(block.acf_fc_layout, payload.id, index);
            return (
              <React.Fragment key={key}>
                <MasonryImages data={payload} />
              </React.Fragment>
            );
          }

          default:
            return null;
        }
      })}
    </>
  );
}